import React, { useState, useEffect } from 'react';
import { Search, MapPin, Trash2, Home, Briefcase, Coffee, Users, Heart, X, Navigation, Calendar, User, ChevronRight, Activity, Clock } from 'lucide-react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface Place {
  id: number;
  name: string;
  address?: string;
  latitude: number;
  longitude: number;
  owner_username: string;
  owner_name?: string;
  family_name: string;
  created_at: string;
  owner_language?: string;
}

const getPlaceIcon = (name: string) => {
  const n = name.toLowerCase();
  if (n.includes('ev') || n.includes('home')) return { Icon: Home, label: 'Ev' };
  if (n.includes('iş') || n.includes('work') || n.includes('ofis')) return { Icon: Briefcase, label: 'İş' };
  if (n.includes('cafe') || n.includes('kafe')) return { Icon: Coffee, label: 'Kafe' };
  if (n.includes('okul') || n.includes('school')) return { Icon: Users, label: 'Okul' };
  if (n.includes('hastane') || n.includes('hospital')) return { Icon: Heart, label: 'Sağlık' };
  return { Icon: MapPin, label: 'Konum' };
};

const getFlag = (lang?: string) => {
  const f: Record<string, string> = { 'tr': '🇹🇷', 'en': '🇬🇧', 'de': '🇩🇪', 'es': '🇪🇸', 'fr': '🇫🇷', 'it': '🇮🇹', 'pt': '🇵🇹', 'nl': '🇳🇱', 'pl': '🇵🇱', 'ru': '🇷🇺', 'ar': '🇸🇦', 'zh': '🇨🇳', 'ja': '🇯🇵', 'ko': '🇰🇷', 'hi': '🇮🇳', 'id': '🇮🇩', 'vi': '🇻🇳', 'th': '🇹🇭' };
  return lang ? f[lang] || '🌐' : null;
};

const mapIcon = () => L.divIcon({
  className: 'marker',
  html: `<div style="width:18px;height:18px;background:#8B5CF6;border:2px solid white;border-radius:50%;"></div>`,
  iconSize: [18, 18], iconAnchor: [9, 9],
});

// Stat Card - Minimal
const StatCard: React.FC<{ icon: React.ReactNode; value: number; label: string; color: string }> = ({ icon, value, label, color }) => {
  const colors: Record<string, string> = {
    green: 'from-green-50 to-green-100 border-green-200 text-green-700',
    blue: 'from-blue-50 to-blue-100 border-blue-200 text-blue-700',
    purple: 'from-purple-50 to-purple-100 border-purple-200 text-purple-700',
    gray: 'from-gray-50 to-gray-100 border-gray-200 text-gray-600',
  };

  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-2.5 text-center`}>
      <div className="flex justify-center mb-1 opacity-60 [&>svg]:w-4 [&>svg]:h-4">
        {icon}
      </div>
      <p className="text-xl font-semibold">{value}</p>
      <p className="text-[10px] opacity-60 uppercase tracking-wide">{label}</p>
    </div>
  );
};

const Places: React.FC = () => {
  const [places, setPlaces] = useState<Place[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [family, setFamily] = useState('all');
  const [deleting, setDeleting] = useState<number | null>(null);
  const [selected, setSelected] = useState<Place | null>(null);
  const [limit, setLimit] = useState(50);

  useEffect(() => { fetchPlaces(); }, []);

  const fetchPlaces = async () => {
    try {
      const r = await axios.get('https://api.lifeshare.app/api/v1/admin/places');
      setPlaces(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const del = async (id: number) => {
    if (!window.confirm('Bu yeri silmek istiyor musunuz?')) return;
    setDeleting(id);
    try {
      await axios.delete(`https://api.lifeshare.app/api/v1/admin/places/${id}`);
      setPlaces(p => p.filter(x => x.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (e) { alert('Silinemedi'); }
    setDeleting(null);
  };

  const families = Array.from(new Set(places.map(p => p.family_name)));
  const filtered = places.filter(p => {
    const q = search.toLowerCase();
    const m = p.name.toLowerCase().includes(q) || p.address?.toLowerCase().includes(q) || p.family_name.toLowerCase().includes(q);
    return m && (family === 'all' || p.family_name === family);
  });

  const homeC = places.filter(p => /ev|home/i.test(p.name)).length;
  const workC = places.filter(p => /iş|work/i.test(p.name)).length;
  const otherC = places.length - homeC - workC;

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Yerler</h1>
              <p className="text-sm text-gray-500 mt-1">{places.length} konum</p>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
          <StatCard icon={<MapPin />} value={places.length} label="Toplam" color="purple" />
          <StatCard icon={<Home />} value={homeC} label="Ev" color="blue" />
          <StatCard icon={<Briefcase />} value={workC} label="İş" color="green" />
          <StatCard icon={<Activity />} value={otherC} label="Diğer" color="gray" />
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-xl text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>

        {/* Filter */}
        <select
          value={family}
          onChange={e => setFamily(e.target.value)}
          className="w-full mb-4 p-3 bg-white rounded-xl text-sm text-gray-700 outline-none focus:ring-2 focus:ring-purple-500"
        >
          <option value="all">Tüm Çevreler</option>
          {families.map(f => <option key={f} value={f}>{f}</option>)}
        </select>

        {/* Places List */}
        <div className="bg-white rounded-2xl divide-y divide-gray-100 overflow-hidden">
          {filtered.slice(0, limit).map(p => {
            const { Icon, label } = getPlaceIcon(p.name);
            const flag = getFlag(p.owner_language);
            
            return (
              <div 
                key={p.id}
                onClick={() => setSelected(p)}
                className="flex items-center gap-3 p-4 active:bg-gray-50 cursor-pointer"
              >
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-medium text-gray-900 truncate">{p.name}</p>
                  <p className="text-xs text-gray-500 truncate">{p.address || p.family_name}</p>
                </div>
                <div className="hidden sm:block text-xs text-gray-400">
                  {p.owner_name || p.owner_username} {flag}
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="p-8 text-center">
              <MapPin className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">Sonuç bulunamadı</p>
            </div>
          )}
        </div>

        {/* Load More */}
        {filtered.length > limit && (
          <button
            onClick={() => setLimit(l => l + 50)}
            className="w-full mt-4 py-3 text-purple-600 text-sm font-medium"
          >
            Daha fazla göster
          </button>
        )}
      </div>

      {/* Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50" onClick={() => setSelected(null)}>
          <div 
            className="bg-white w-full sm:max-w-md sm:rounded-2xl max-h-[85vh] overflow-auto rounded-t-3xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Handle */}
            <div className="sm:hidden flex justify-center pt-2 pb-1">
              <div className="w-9 h-1 bg-gray-300 rounded-full" />
            </div>

            {/* Header */}
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  {React.createElement(getPlaceIcon(selected.name).Icon, { className: "w-5 h-5 text-purple-600" })}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{selected.name}</p>
                  <p className="text-xs text-gray-500">{getPlaceIcon(selected.name).label}</p>
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            {/* Map */}
            <div className="h-36 mx-4 rounded-xl overflow-hidden">
              <MapContainer center={[selected.latitude, selected.longitude]} zoom={15} style={{ height: '100%', width: '100%' }} zoomControl={false}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={[selected.latitude, selected.longitude]} icon={mapIcon()} />
              </MapContainer>
            </div>

            {/* Info */}
            <div className="p-4 space-y-3">
              {selected.address && (
                <div className="flex gap-3">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500">Adres</p>
                    <p className="text-sm text-gray-900">{selected.address}</p>
                  </div>
                </div>
              )}
              <div className="flex gap-3">
                <User className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Oluşturan</p>
                  <p className="text-sm text-gray-900">{selected.owner_name || selected.owner_username}</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Users className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Çevre</p>
                  <p className="text-sm text-gray-900">{selected.family_name}</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Calendar className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Tarih</p>
                  <p className="text-sm text-gray-900">{new Date(selected.created_at).toLocaleDateString('tr-TR')}</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="p-4 pt-0 space-y-2">
              <button
                onClick={() => window.open(`https://www.google.com/maps?q=${selected.latitude},${selected.longitude}`, '_blank')}
                className="w-full h-11 bg-purple-600 text-white rounded-xl font-medium text-sm flex items-center justify-center gap-2"
              >
                <Navigation className="w-4 h-4" />
                Haritada Aç
              </button>
              <button
                onClick={() => { del(selected.id); setSelected(null); }}
                disabled={deleting === selected.id}
                className="w-full h-11 text-red-500 font-medium text-sm flex items-center justify-center gap-2"
              >
                {deleting === selected.id ? (
                  <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Places;
